/**
 * File: apps/qbench-cp/reactapp/src/context/fields.js
 *
 * Purpose: Provides the FieldProvider context and useFieldConfig hook that centralize field
 *     configuration retrieval, default-value generation, and entity/form data transformation.
 *
 * Scope: Field configuration context consumed by FormV2 and any component rendering
 *     entity-driven forms across the customer portal.
 *
 * Tags: customer-portal, field-config
 *
 * Overview: Loads per-division field configuration via useFieldsByDivision and exposes
 *     getFields to filter it down by entity type, division, order_request_field visibility, and
 *     an explicit fieldsToShow/fieldsToMerge override list. getDefaultValues builds a
 *     react-hook-form default-values object per field, falling back to each field's portal
 *     default and normalizing missing values to empty strings. processEntity transforms an
 *     entity's raw field values between server and client representations using each field
 *     type's transform functions, merging in any custom key remapping a transform returns.
 *     Also mirrors the context value into a module-level variable so getFields/processEntity/
 *     getDefaultValues can be called outside of a React render (e.g. from plain utility code).
 *
 * Dependencies: shared/components/FormV2/Base (fieldTypeToComponentMap for transform functions).
 *
 * Exports: FieldProvider — the context provider component; useFieldConfig — hook returning the
 *     field-config context; getFields, processEntity, getDefaultValues — module-level functions
 *     backed by the last-rendered provider's context, usable outside of React components.
 */

import { useAppStore } from 'App/store';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { fieldTypeToComponentMap } from 'shared/components/FormV2/Base';
import { EntityType } from 'shared/constants/entityTypes';
import { AddlFieldType } from 'shared/constants/fields';
import { useFieldsByDivision } from 'shared/hooks/api/fields';
import { isNonArrayObject, isNotEmpty, pick } from 'shared/utils/functions';
import Loading from '../shared/layout/Loading';

let moduleStore = null;

/**
 * Context provider for field management and form configuration.
 * Primarily used for `<Form>` and provides centralized utilities for:
 * - Field configuration retrieval (global and division-specific).
 * - Default value generation.
 * - Data transformation between client/server formats.
 * - Form field rendering.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 */
function FieldProvider({ children }) {
  const { appState } = useAppStore();
  const { divisionId: activeDivisionId, globalDivisionId } = appState;
  const { data: fieldConfig, isLoading } = useFieldsByDivision();

  /**
   * Retrieve field configurations for a Division.
   * @param {import('../shared/components/FormV2/types').GetFieldsOptions} options
   * @returns {Array<Object>} Array of field configurations.
   */
  const getFields = useCallback(
    ({
      entityType,
      useGlobal = false,
      showAllFields = false,
      divisionId = activeDivisionId,
      fieldsToShow = [],
      fieldsToMerge = [],
    }) => {
      const isCustomerOrContact = [
        // The LIMS `order_request_field` toggle is not available
        // for these entities. So show all fields by default.
        EntityType.CUSTOMER,
        EntityType.INVOICE,
        EntityType.CONTACT,
      ].includes(entityType);
      const shouldShowAllFields = isCustomerOrContact || showAllFields;
      const existingFields = get(
        fieldConfig,
        [useGlobal ? globalDivisionId : divisionId, 'fields', entityType],
        [],
      )
        .filter((field) => shouldShowAllFields || field?.orderRequestField)
        .filter((field) => field.fieldType !== AddlFieldType.DUMMY)
        .map((field) => {
          if (isEmpty(fieldsToMerge)) return field;
          const fieldToMerge = fieldsToMerge.find((f) => f.name === field.name);
          return { ...field, ...fieldToMerge };
        });
      const newFields = fieldsToMerge.filter(
        (f) => !existingFields.map((f) => f.name).includes(f.name),
      );

      return [...existingFields, ...newFields].filter(
        (field) => isEmpty(fieldsToShow) || fieldsToShow.includes(field.name),
      );
    },
    [fieldConfig, activeDivisionId, globalDivisionId],
  );

  /**
   * Retrieve a list of attachment entities for a Division.
   * @param {import('../shared/components/FormV2/types').GetAttachmentEntitiesOptions} options
   * @returns {Array<Object>} Array of entity types.
   */
  const getAttachmentEntities = useCallback(
    ({ divisionId = activeDivisionId, useGlobal = false }) =>
      get(
        fieldConfig,
        [useGlobal ? globalDivisionId : divisionId, 'attachmentEntities'],
        [],
      ),
    [activeDivisionId, fieldConfig, globalDivisionId],
  );

  /**
   * Generate a default value object for passing into react-hook-form `useForm()`
   * when`<Form>` is in Create Mode. Default values set in LIMS CP Settings will be applied.
   * @param {import('../shared/components/FormV2/types').GetDefaultValuesOptions} options
   * @returns {import('react-hook-form').DefaultValues<Object>} The default values object.
   */
  const getDefaultValues = useCallback(
    ({ entity, ...fieldsOptions }) => {
      const isEditing = !isEmpty(entity);
      return getFields(fieldsOptions).reduce((acc, field) => {
        const FormComponent = fieldTypeToComponentMap[field.fieldType];
        const defaultValue = isEditing
          ? entity[field.name]
          : field.portalDefaultValue;
        return {
          ...acc,
          [field.name]:
            isNil(defaultValue) && !field.fieldTypeEntityReference
              ? // Note: react-hook-form prefers empty defaults to be empty string.
                // https://react-hook-form.com/docs/useform#defaultValues
                ''
              : FormComponent.transform.defaultValue(defaultValue, {
                  field: { ...field, entityType: fieldsOptions.entityType },
                  payload: entity,
                }),
        };
      }, {});
    },
    [getFields],
  );

  /**
   * Transform entity fields between different modes (server/client).
   * Runs when <Form> initializes with/without an entity, i.e. Create/Edit mode,
   * and also during the submit process, before payload is sent to the server.
   * @param {import('../shared/components/FormV2/types').ProcessEntityOptions} options
   * @returns {Object} The transformed entity object.
   */
  const processEntity = useCallback(
    ({ entity, mode = 'fromServer', ...fieldsOptions }) => {
      const fields = getFields(fieldsOptions);
      const values = pick(
        entity,
        fields.map((f) => f.name),
        /_data_ent_name_map$/,
      );
      const result = fields.reduce((acc, field) => {
        const value = values[field.name];
        const FormComponent = fieldTypeToComponentMap[field.fieldType];
        const val = FormComponent.transform[mode](value, {
          field: { ...field, entityType: fieldsOptions.entityType },
          payload: values,
        });
        return {
          ...acc,
          ...(isNonArrayObject(val) && isNotEmpty(val)
            ? // Transform functions can return objects with different key names than the field name
              // (e.g., tag → tag_values, additional_customers → additional_customer_ids).
              // This gives form components full control over value transformation.
              val
            : // Otherwise, we are dealing with a simple transformed value,
              // so use the field name as the key.
              { [field.name]: isNil(value) ? value : val }),
        };
      }, {});
      return result;
    },
    [getFields],
  );

  const contextValue = useMemo(
    () => ({
      getFields,
      processEntity,
      getDefaultValues,
      getAttachmentEntities,
    }),
    [getFields, getDefaultValues, getAttachmentEntities, processEntity],
  );

  useEffect(() => {
    moduleStore = contextValue;
  }, [contextValue]);

  return (
    <FieldContext.Provider value={{ ...contextValue, isLoading }}>
      {!moduleStore ? <Loading dim fullScreen /> : children}
    </FieldContext.Provider>
  );
}

function useFieldConfig() {
  const context = useContext(FieldContext);
  if (context === undefined) {
    throw new Error('useFieldConfig must be used within a FieldProvider');
  }
  return context;
}

/**
 * Provides access to context-level field management utilities.
 * @type {React.Context<import('../shared/components/FormV2/types').FieldContextValue>}
 */
const FieldContext = React.createContext(undefined);
FieldContext.displayName = 'FieldContext';

export const getFields = (...args) => moduleStore?.getFields(...args);
export const processEntity = (...args) => moduleStore?.processEntity(...args);
export const getDefaultValues = (...args) =>
  moduleStore?.getDefaultValues(...args);
export { FieldProvider, useFieldConfig };
